import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ApplicationApi, ApplicationTemplateApi } from "../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../services/Api/api";
import AllApps from "../../components/Apps/AllApps";
import InstalledApps from "../../components/Apps/InstalledApps";
import OrganizationAppsEditPage from "../Edit";

interface MatchParams {
	appId: string;
}

const OrganizationAppsAllPage = (props: RouteComponentProps) => {
	const [apps, setApps] = useState<undefined | ApplicationApi[]>(undefined);
	const [appTemplates, setAppTemplates] = useState<undefined | ApplicationTemplateApi[]>(undefined);
	const { addFlash } = useFlash();
	const history = useHistory();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation();
	const { appId } = useParams<MatchParams>();
	const organization = useSelector(selectOrganization);
	const modalClosedPathname = `/${organization.id}/apps/all`;

	useEffect(() => {
		handleChangeTabTitle(t("modules.app.field.all.title"));
		Promise.all([api.organization().getApps({ include: "settings" }), api.organization().getAppTemplates()])
			.then((res) => {
				setApps(res[0]);
				setAppTemplates(res[1]);
			})
			.catch((err) => {
				handleError.alert(err, addFlash);
			});
	}, []);

	const handleUninstall = (app: ApplicationApi) => {
		history.push(modalClosedPathname);
		setApps(apps?.filter((f) => f.id !== app.id));
	};

	const handleUpdate = (app: ApplicationApi) => {
		const currApps = apps;
		if (currApps) {
			const currentAppIndex = currApps.findIndex((f) => f.id === app.id);
			currApps[currentAppIndex] = app;
			setApps([...currApps]);
		}
	};

	const handleUpdateNewInstalledApp = (app: ApplicationApi) => {
		const currentApp = apps?.find((f) => f.id === app.id);
		if (!currentApp) setApps([...(apps || []), { ...app }]);
		history.push(`${props?.match.url}/${app.id}`);
	};

	const onHideModal = () => history.push(modalClosedPathname);

	if (!apps || !appTemplates) {
		return <LoadingContainer />;
	}

	return (
		<>
			{apps.length > 0 && (
				<InstalledApps
					handleUpdate={handleUpdate}
					handleUninstall={handleUninstall}
					routeProps={props}
					installedApps={apps}
					appTemplates={appTemplates}
				/>
			)}
			<AllApps appTemplates={appTemplates} handleUpdateNewInstalledApp={handleUpdateNewInstalledApp} />
			{appId && (
				<OrganizationAppsEditPage
					handleUpdate={handleUpdate}
					handleUninstall={handleUninstall}
					routeProps={props}
					installedApps={apps}
					appConfigs={appTemplates}
					appId={appId}
					onHideModal={onHideModal}
				/>
			)}
		</>
	);
};
export default OrganizationAppsAllPage;
